import React, { useEffect, useMemo, useState } from "react";

import {
  MaterialReactTable,
  type MRT_ColumnDef,
} from "material-react-table";
import {
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  Divider,
  Paper,
  Stack,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";
import CheckCircleOutlineIcon from "@mui/icons-material/CheckCircleOutline";
import RadioButtonUncheckedIcon from "@mui/icons-material/RadioButtonUnchecked";
import { useDispatch } from "react-redux";
import { fetchGroups } from "../../../../../features/slices/UserGroups";
import { AppDispatch } from "../../../../../store";

interface UserGroup {
  id: number;
  title: string;
  permissions: {
    read: boolean;
    create: boolean;
    delete: boolean;
    update: boolean;
  };
}

const permissionLabels: Array<{
  key: keyof UserGroup["permissions"];
  label: string;
}> = [
  { key: "read", label: "Read" },
  { key: "create", label: "Create" },
  { key: "delete", label: "Delete" },
  { key: "update", label: "Update" },
];

const defaultPermissions: UserGroup["permissions"] = {
  read: false,
  create: false,
  delete: false,
  update: false,
};

const toBooleanPermission = (value: unknown) => value === true || value === "true";

const convertPermissions = (groups: unknown): UserGroup[] =>
  (Array.isArray(groups) ? groups : []).map((group: any) => ({
    ...group,
    title: group?.title ?? "Untitled group",
    permissions: {
      read: toBooleanPermission(group?.permissions?.read),
      create: toBooleanPermission(group?.permissions?.create),
      delete: toBooleanPermission(group?.permissions?.delete),
      update: toBooleanPermission(group?.permissions?.update),
    },
  }));
const UserGroups = () => {
  const dispatch = useDispatch<AppDispatch>();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const [data, setData] = useState<UserGroup[]>([]);
  const [isError, setIsError] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [isRefetching, setIsRefetching] = useState(false);
  const totalPermissions = data.reduce(
    (total, group) =>
      total +
      permissionLabels.filter(
        ({ key }) => (group.permissions ?? defaultPermissions)[key]
      ).length,
    0
  );

  useEffect(() => {
    let isMounted = true;

    const fetchData = async () => {
      setIsLoading(true);
      setIsRefetching(true);

      try {
        const groups = await dispatch(fetchGroups()).unwrap();
        if (isMounted) {
          setData(convertPermissions(groups));
          setIsError(!Array.isArray(groups));
          if (!Array.isArray(groups)) {
            console.warn("Unexpected user groups response", groups);
          }
        }
      } catch (error) {
        if (isMounted) {
          setIsError(true);
          setData([]);
        }
      } finally {
        if (isMounted) {
          setIsLoading(false);
          setIsRefetching(false);
        }
      }
    };

    fetchData();

    return () => {
      isMounted = false;
    };
  }, [dispatch]);

  const columns = useMemo<MRT_ColumnDef<UserGroup>[]>(
    () => [
      {
        accessorKey: "id",
        header: "ID",
      },
      {
        accessorKey: "title",
        header: "User Group",
      },
      {
        accessorKey: "permissions.read",
        header: "Read",
        Cell: ({ row }) => (
          <PermissionCell enabled={row.original.permissions.read} />
        ),
      },
      {
        accessorKey: "permissions.create",
        header: "Create",
        Cell: ({ row }) => (
          <PermissionCell enabled={row.original.permissions.create} />
        ),
      },
      {
        accessorKey: "permissions.delete",
        header: "Delete",
        Cell: ({ row }) => (
          <PermissionCell enabled={row.original.permissions.delete} />
        ),
      },
      {
        accessorKey: "permissions.update",
        header: "Update",
        Cell: ({ row }) => (
          <PermissionCell enabled={row.original.permissions.update} />
        ),
      },
    ],
    []
  );

  const header = (
    <Box
      sx={{
        alignItems: { xs: "stretch", sm: "center" },
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        gap: 1.5,
        justifyContent: "space-between",
        mb: { xs: 1.5, sm: 2 },
      }}
    >
      <Box sx={{ minWidth: 0 }}>
        <Typography
          component="h1"
          sx={{ fontSize: { xs: 20, sm: 24 }, fontWeight: 800, lineHeight: 1.2 }}
        >
          User groups
        </Typography>
        <Typography color="text.secondary" sx={{ fontSize: 13, mt: 0.5 }}>
          Permission matrix
        </Typography>
      </Box>

      <Stack
        direction="row"
        spacing={1}
        sx={{
          flexWrap: "wrap",
          justifyContent: { xs: "flex-start", sm: "flex-end" },
          rowGap: 1,
        }}
      >
        <Chip
          label={`${data.length} groups`}
          size="small"
          sx={{ borderRadius: 1, fontWeight: 700 }}
        />
        <Chip
          label={`${totalPermissions}/${data.length * permissionLabels.length || 0} enabled`}
          size="small"
          color="success"
          variant="outlined"
          sx={{ borderRadius: 1, fontWeight: 700 }}
        />
      </Stack>
    </Box>
  );

  if (isMobile) {
    return (
      <Stack spacing={1.25}>
        {header}
        {isLoading && (
          <Paper variant="outlined" sx={{ borderRadius: 1, py: 4 }}>
            <Box sx={{ display: "flex", justifyContent: "center" }}>
              <CircularProgress size={24} />
            </Box>
          </Paper>
        )}
        {!isLoading && data.length === 0 && (
          <Card variant="outlined" sx={{ borderRadius: 1 }}>
            <CardContent>
              <Typography color="text.secondary" fontSize={14}>
                No user groups found
              </Typography>
            </CardContent>
          </Card>
        )}
        {data.map((group) => (
          <Card
            key={group.id}
            variant="outlined"
            sx={{
              borderColor: "divider",
              borderRadius: 1,
              boxShadow: "0 1px 2px rgba(16, 24, 40, 0.04)",
              overflow: "hidden",
            }}
          >
            <CardContent sx={{ p: 0, "&:last-child": { pb: 0 } }}>
              <Box
                sx={{
                  alignItems: "center",
                  bgcolor: "background.level1",
                  display: "flex",
                  gap: 1,
                  justifyContent: "space-between",
                  px: 1.5,
                  py: 1.25,
                }}
              >
                <Box sx={{ minWidth: 0 }}>
                  <Typography
                    sx={{
                      fontSize: 15,
                      fontWeight: 700,
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                      whiteSpace: "nowrap",
                    }}
                  >
                    {group.title}
                  </Typography>
                  <Typography color="text.secondary" fontSize={12}>
                    ID {group.id}
                  </Typography>
                </Box>
                <Chip
                  size="small"
                  label={`${
                    permissionLabels.filter(
                      ({ key }) => (group.permissions ?? defaultPermissions)[key]
                    ).length
                  }/4`}
                  color={
                    permissionLabels.every(
                      ({ key }) => (group.permissions ?? defaultPermissions)[key]
                    )
                      ? "success"
                      : "default"
                  }
                  variant="outlined"
                  sx={{ borderRadius: 1, flexShrink: 0, fontWeight: 700 }}
                />
              </Box>

              <Divider />

              <Box
                sx={{
                  display: "grid",
                  gap: 1,
                  gridTemplateColumns: "repeat(2, minmax(0, 1fr))",
                  p: 1.5,
                }}
              >
                {permissionLabels.map(({ key, label }) => {
                  const enabled = (group.permissions ?? defaultPermissions)[key];
                  return (
                    <Box
                      key={key}
                      sx={{
                        alignItems: "center",
                        bgcolor: enabled ? "#ecfdf3" : "#f8fafc",
                        border: "1px solid",
                        borderColor: enabled ? "#abefc6" : "divider",
                        borderRadius: 1,
                        color: enabled ? "success.dark" : "text.secondary",
                        display: "flex",
                        gap: 0.75,
                        minHeight: 40,
                        px: 1.25,
                      }}
                    >
                      {enabled ? (
                        <CheckCircleOutlineIcon sx={{ fontSize: 18 }} />
                      ) : (
                        <RadioButtonUncheckedIcon sx={{ fontSize: 18 }} />
                      )}
                      <Typography fontSize={13} fontWeight={700} noWrap>
                        {label}
                      </Typography>
                    </Box>
                  );
                })}
              </Box>
            </CardContent>
          </Card>
        ))}
      </Stack>
    );
  }

  return (
    <Stack spacing={2}>
      {header}
      <MaterialReactTable
        columns={columns}
        data={data}
        state={{
          isLoading,
          showAlertBanner: isError,
          showProgressBars: isRefetching,
        }}
        enableColumnActions={false}
        enableDensityToggle={false}
        enableFullScreenToggle={false}
        enableRowSelection={false}
        enableSorting
        initialState={{ density: "compact" }}
        muiTablePaperProps={{
          sx: { width: "100%", overflow: "hidden" },
        }}
        muiTableContainerProps={{ sx: { maxHeight: "70vh", minWidth: 640 } }}
        muiToolbarAlertBannerProps={{
          color: isError ? "error" : "info",
          children: isError
            ? "Could not load user groups"
            : "Permission matrix is read-only here",
        }}
      />
    </Stack>
  );
};

const PermissionCell = ({ enabled }: { enabled: boolean }) => (
  <Chip
    icon={
      enabled ? (
        <CheckCircleOutlineIcon sx={{ fontSize: "18px !important" }} />
      ) : (
        <RadioButtonUncheckedIcon sx={{ fontSize: "18px !important" }} />
      )
    }
    label={enabled ? "Enabled" : "Off"}
    size="small"
    color={enabled ? "success" : "default"}
    variant={enabled ? "outlined" : "filled"}
    sx={{
      borderRadius: 1,
      fontWeight: 700,
      minWidth: 88,
      "& .MuiChip-icon": { ml: 0.75 },
    }}
  />
);

export default UserGroups;
